import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail } from "./api";
import { LogoMark } from "../components/site/Shared";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#060A14] flex items-center justify-center px-5 blueprint-grid">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <LogoMark className="h-10 w-10" />
          <div className="leading-none">
            <div className="font-display font-extrabold text-white text-lg tracking-tight">
              A3<span style={{ color: "#00F0FF" }}>NETWORKS</span>
            </div>
            <div className="mono-label text-[9px] mt-1" style={{ color: "#64748B" }}>HR Management Portal</div>
          </div>
        </div>
        <div className="bg-[#0C1322] border border-white/10 p-8">
          <h1 className="font-display font-extrabold text-2xl text-white tracking-tight">{title}</h1>
          <p className="text-sm mt-2 mb-7" style={{ color: "#94A3B8" }}>{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Portal Login" subtitle="Field Engineers & Admins — sign in to your A3NETWORKS workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Email</label>
          <input type="email" required className="field" placeholder="you@a3networks.co.in"
            value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div>
          <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>Password</label>
          <input type="password" required className="field" placeholder="••••••••"
            value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="group w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3.5 mono-label transition-colors duration-300 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <>Sign In <ArrowRight size={16} className="cta-arrow" /></>}
        </button>
      </form>
      <div className="mt-5 flex items-center justify-between">
        <Link to="/portal/forgot-password" className="mono-label hover:text-cyan transition-colors" style={{ color: "#64748B" }} data-testid="forgot-link">
          Forgot password?
        </Link>
        <Link to="/" className="mono-label hover:text-cyan transition-colors" style={{ color: "#64748B" }}>
          ← Back to site
        </Link>
      </div>
    </AuthShell>
  );
}

export { AuthShell };
